import type { TableColumnsDto } from 'cadenza/base-table/model/table-columns-dto';
import type { ConditionType } from 'cadenza/model/condition-type.gen';
import type { Context } from 'cadenza/query-language/context';
import { Printer } from 'cadenza/query-language/printer';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto, WorkbookQlConditionValueItemDto } from 'cadenza/workbook/model/condition-dto';
import type { QlConditionVariableDto } from 'cadenza/workbook/model/ql-condition-variable-dto';

const CONDITION_TYPES_CONVERTIBLE_TO_QL = [
  'TableCondition',
  'DateCondition',
  'ValueIntervalCondition',
  'DateIntervalCondition',
];

export function serializeFilterValue(variable: QlConditionVariableDto, value: unknown) {
  // Value returned here must be compatible to the PublishedAttributeValueJsSerializer
  // which is used for deserializing, e.g. LONG needs to be passed as a String
  if (value instanceof Date) {
    return value.toISOString();
  } else if (variable.type === 'LONG' || variable.type === 'INT' || variable.type === 'DOUBLE') {
    let parsedValue;
    if (typeof value === 'string' && variable.type === 'DOUBLE') {
      parsedValue = parseFloat(value);
    } else if (typeof value === 'string') {
      parsedValue = parseInt(value);
    } else {
      parsedValue = value as number | null;
    }
    if (parsedValue !== null && variable.type === 'LONG') {
      return parsedValue.toFixed();
    }
    return parsedValue;
  }
  return value;
}

/**
 * Returns complete condition value.
 */
export function getQlConditionValue(condition: Pick<ConditionDto, 'values'>) {
  return condition?.values?.valueItems[0] as WorkbookQlConditionValueItemDto;
}

/** Returns string representation of an expression used as advanced condition value. */
export function formatQlExpressionAsString(context: Context, condition: Pick<ConditionDto, 'values'>) {
  const qlCondition = getQlConditionValue(condition);
  return qlCondition ? new Printer(context).print(qlCondition.expression) : '';
}

export function getQlConditionVariables(condition?: Pick<ConditionDto, 'values'>) {
  return (condition && getQlConditionValue(condition)?.variables) ?? [];
}

export function getQlVariable(condition?: ConditionDto) {
  return getQlConditionVariables(condition)?.[0];
}

export function isConditionTypeConvertibleToQl(conditionType: ConditionType) {
  return CONDITION_TYPES_CONVERTIBLE_TO_QL.includes(conditionType);
}

/**
 * Checks if every column is visible, has an attributeType other than 'IPADDRESS'
 * and checks if its name occurs in acceptedAttributes (id check would be an improvement).
 */
export function areConditionTableColumnsAccepted(tableColumnsDefinitionDto?: TableColumnsDto) {
  return (
    tableColumnsDefinitionDto?.columns.every((column) => {
      return column.attributeType !== 'IPADDRESS';
    }) ?? false
  ); // In case tableColumnsDefinitionDto is undefined
}

export function isAttributeRelationalNameAttribute(objecttype: Objecttype, attributeId: AttributeId) {
  return objecttype.attributeRelationGroups.some((relation) => relation.nameAttribute === attributeId);
}

export function areConditionAttributeIdsAccepted(
  acceptedAttributes: WorkbookAttributeDto[],
  ...attributeIds: (AttributeId | undefined)[]
) {
  return attributeIds.every((attributeId) =>
    acceptedAttributes.some((acceptedAttribute) => attributeId === acceptedAttribute.id),
  );
}
